import React from 'react';
import { Users, ArrowRight, Heart, MapPin, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [isInView, setIsInView] = React.useState(false);
  React.useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.2 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return isInView;
}

export function PvtmatesPromo() {
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef);

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`relative overflow-hidden rounded-3xl transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1539635278303-d4002c07eae3?q=80&w=2670&auto=format&fit=crop"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60" />
          </div>

          <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-0">
            <div className="p-8 sm:p-12 lg:p-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/20 text-rose-300 text-sm font-medium mb-6">
                <Heart className="w-4 h-4" />
                <span>PVTmates</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Trouvez votre compagnon de PVT
              </h2>

              <p className="text-lg text-gray-300 mb-8 max-w-lg">
                Rencontrez d'autres voyageurs qui partagent vos dates, votre destination et vos centres d'int&eacute;r&ecirc;t.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-rose-400" />
                  </div>
                  <span className="text-sm">M&ecirc;me destination</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-rose-400" />
                  </div>
                  <span className="text-sm">M&ecirc;mes dates</span>
                </div>
              </div>

              <Link
                to="/pvtmates"
                className="inline-flex items-center gap-2 px-6 py-3 bg-rose-500 hover:bg-rose-600 text-white rounded-xl font-medium shadow-lg shadow-rose-500/20 hover:shadow-xl hover:shadow-rose-500/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                <Users className="w-5 h-5" />
                <span>D&eacute;couvrir PVTmates</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="hidden lg:flex items-center justify-center p-12">
              <div className="relative">
                <div className="absolute -inset-8 bg-rose-500/10 rounded-full blur-3xl" />
                <div className="relative space-y-4">
                  {[
                    { name: 'Marie', dest: 'Australie', flag: '\ud83c\udde6\ud83c\uddfa', time: 'D\u00e9part en mars' },
                    { name: 'Thomas', dest: 'Canada', flag: '\ud83c\udde8\ud83c\udde6', time: 'D\u00e9part en mai' },
                    { name: 'L\u00e9a', dest: 'Japon', flag: '\ud83c\uddef\ud83c\uddf5', time: 'D\u00e9part en sept.' },
                  ].map((profile, i) => (
                    <div
                      key={profile.name}
                      className="flex items-center gap-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-4 hover:bg-white/15 transition-all duration-300"
                      style={{ transform: `translateX(${i * 20}px)` }}
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center text-white font-bold">
                        {profile.name[0]}
                      </div>
                      <div className="flex-1">
                        <div className="text-white font-medium text-sm">{profile.name}</div>
                        <div className="text-white/60 text-xs">{profile.flag} {profile.dest} &middot; {profile.time}</div>
                      </div>
                      <Heart className="w-5 h-5 text-rose-400" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
